import { releaseYearToDecade } from "../hints.js";
import { asTrimmedString, asYear, textsMatch } from "./normalize.js";
function hasText(value) {
    return typeof value === "string" && value.trim().length > 0;
}
export function decadeStart(year) {
    return releaseYearToDecade(year);
}
export function decadeChoiceId(decade) {
    return String(decade);
}
export function decadeLabel(decade) {
    return `${decade}s`;
}
function uniqueSorted(values) {
    return [...new Set(values.map((v) => v.trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b, "fr", { sensitivity: "base" }));
}
function metadataChoices(songs, song, field, max = 8) {
    const all = uniqueSorted(songs.map((s) => s[field] ?? "").filter(Boolean));
    const correct = song?.[field]?.trim();
    if (all.length <= max) {
        return all.map((v) => ({ id: v, label: v }));
    }
    const pool = all.filter((v) => v !== correct);
    const picked = [];
    const step = Math.max(1, Math.floor(pool.length / (max - 1)));
    for (let i = 0; i < pool.length && picked.length < max - 1; i += step) {
        picked.push(pool[i]);
    }
    if (correct && !picked.includes(correct))
        picked.unshift(correct);
    return uniqueSorted(picked)
        .slice(0, max)
        .map((v) => ({ id: v, label: v }));
}
function yearInRange(songYear, answer, delta) {
    const year = asYear(answer);
    if (year === null)
        return false;
    return year >= songYear - delta && year <= songYear + delta;
}
const DECADE = {
    id: "DECADE",
    name: "Décennie",
    icon: "🟣",
    description: "Quelle décennie ?",
    color: "#a855f7",
    answerType: "CHOICE",
    difficulty: "easy",
    requiredMetadata: ["releaseYear"],
    implemented: true,
    isCompatible: (song) => Number.isInteger(song.releaseYear),
    getChoices: (ctx) => {
        const decades = new Set();
        for (const song of ctx.songs)
            decades.add(decadeStart(song.releaseYear));
        if (decades.size === 0) {
            for (let y = 1960; y <= 2020; y += 10)
                decades.add(y);
        }
        return [...decades]
            .sort((a, b) => a - b)
            .map((d) => ({ id: decadeChoiceId(d), label: decadeLabel(d) }));
    },
    getCorrectAnswer: (song) => decadeChoiceId(decadeStart(song.releaseYear)),
    validateAnswer: (song, answer) => String(answer) === decadeChoiceId(decadeStart(song.releaseYear)),
    formatAnswer: (answer) => {
        const n = Number(answer);
        return Number.isFinite(n) ? decadeLabel(n) : String(answer ?? "");
    },
    getPublicReveal: (song) => ({
        label: "Bonne réponse",
        value: decadeLabel(decadeStart(song.releaseYear)),
    }),
};
const YEAR_PM2 = {
    id: "YEAR_PM2",
    name: "Année ±2",
    icon: "🟢",
    description: "Donnez l'année, à 2 ans près",
    color: "#22c55e",
    answerType: "YEAR",
    difficulty: "easy",
    requiredMetadata: ["releaseYear"],
    implemented: true,
    isCompatible: (song) => Number.isInteger(song.releaseYear),
    getCorrectAnswer: (song) => song.releaseYear,
    validateAnswer: (song, answer) => yearInRange(song.releaseYear, answer, 2),
    formatAnswer: (answer) => String(asYear(answer) ?? answer ?? ""),
    getPublicReveal: (song) => ({
        label: "Année acceptée",
        value: `${song.releaseYear - 2} – ${song.releaseYear + 2}`,
    }),
    isExactYearBonus: (song, answer) => asYear(answer) === song.releaseYear,
};
const YEAR_PM4 = {
    id: "YEAR_PM4",
    name: "Année ±4",
    icon: "🟡",
    description: "Donnez l'année, à 4 ans près",
    color: "#f59e0b",
    answerType: "YEAR",
    difficulty: "easy",
    requiredMetadata: ["releaseYear"],
    implemented: true,
    isCompatible: (song) => Number.isInteger(song.releaseYear),
    getCorrectAnswer: (song) => song.releaseYear,
    validateAnswer: (song, answer) => yearInRange(song.releaseYear, answer, 4),
    formatAnswer: (answer) => String(asYear(answer) ?? answer ?? ""),
    getPublicReveal: (song) => ({
        label: "Année acceptée",
        value: `${song.releaseYear - 4} – ${song.releaseYear + 4}`,
    }),
    isExactYearBonus: (song, answer) => asYear(answer) === song.releaseYear,
};
const YEAR_EXACT = {
    id: "YEAR_EXACT",
    name: "Année exacte",
    icon: "🎯",
    description: "L'année de sortie, pile",
    color: "#f97316",
    answerType: "YEAR",
    difficulty: "expert",
    requiredMetadata: ["releaseYear"],
    implemented: true,
    isCompatible: (song) => Number.isInteger(song.releaseYear),
    getCorrectAnswer: (song) => song.releaseYear,
    validateAnswer: (song, answer) => asYear(answer) === song.releaseYear,
    formatAnswer: (answer) => String(asYear(answer) ?? answer ?? ""),
    getPublicReveal: (song) => ({
        label: "Bonne réponse",
        value: String(song.releaseYear),
    }),
};
const BEFORE_2000 = {
    id: "BEFORE_2000",
    name: "Avant / après 2000",
    icon: "🔵",
    description: "La chanson est-elle sortie avant 2000 ?",
    color: "#3b82f6",
    answerType: "CHOICE",
    difficulty: "easy",
    requiredMetadata: ["releaseYear"],
    implemented: true,
    isCompatible: (song) => Number.isInteger(song.releaseYear),
    getChoices: () => [
        { id: "before", label: "Avant 2000" },
        { id: "after", label: "2000 ou après" },
    ],
    getCorrectAnswer: (song) => (song.releaseYear < 2000 ? "before" : "after"),
    validateAnswer: (song, answer) => String(answer) === (song.releaseYear < 2000 ? "before" : "after"),
    formatAnswer: (answer) => (String(answer) === "before" ? "Avant 2000" : "2000 ou après"),
    getPublicReveal: (song) => ({
        label: "Bonne réponse",
        value: song.releaseYear < 2000 ? "Avant 2000" : "2000 ou après",
    }),
};
const SOLO_GROUP = {
    id: "SOLO_GROUP",
    name: "Solo / Groupe",
    icon: "🟠",
    description: "Artiste solo ou groupe ?",
    color: "#f97316",
    answerType: "CHOICE",
    difficulty: "easy",
    requiredMetadata: ["artistType"],
    implemented: true,
    isCompatible: (song) => song.artistType === "solo" || song.artistType === "group",
    getChoices: () => [
        { id: "solo", label: "Solo" },
        { id: "group", label: "Groupe" },
    ],
    getCorrectAnswer: (song) => song.artistType,
    validateAnswer: (song, answer) => String(answer) === song.artistType,
    formatAnswer: (answer) => (String(answer) === "group" ? "Groupe" : "Solo"),
    getPublicReveal: (song) => ({
        label: "Bonne réponse",
        value: song.artistType === "group" ? "Groupe" : "Solo",
    }),
};
const ARTIST = {
    id: "ARTIST",
    name: "Artiste",
    icon: "🎤",
    description: "Quel est l'artiste ?",
    color: "#06b6d4",
    answerType: "TEXT",
    difficulty: "expert",
    requiredMetadata: ["artist"],
    implemented: true,
    isCompatible: (song) => hasText(song.artist),
    getCorrectAnswer: (song) => song.artist,
    validateAnswer: (song, answer) => {
        const given = asTrimmedString(answer);
        return given ? textsMatch(song.artist, given) : false;
    },
    formatAnswer: (answer) => asTrimmedString(answer) ?? "",
    getPublicReveal: (song) => ({
        label: "Bonne réponse",
        value: song.artist,
    }),
};
const TITLE = {
    id: "TITLE",
    name: "Titre",
    icon: "🎵",
    description: "Quel est le titre ?",
    color: "#eab308",
    answerType: "TEXT",
    difficulty: "expert",
    requiredMetadata: ["title"],
    implemented: true,
    isCompatible: (song) => hasText(song.title),
    getCorrectAnswer: (song) => song.title,
    validateAnswer: (song, answer) => {
        const given = asTrimmedString(answer);
        return given ? textsMatch(song.title, given) : false;
    },
    formatAnswer: (answer) => asTrimmedString(answer) ?? "",
    getPublicReveal: (song) => ({
        label: "Bonne réponse",
        value: song.title,
    }),
};
function makeMetadataCategory(id, name, icon, color, field, description) {
    return {
        id,
        name,
        icon,
        description,
        color,
        answerType: "CHOICE",
        difficulty: "normal",
        requiredMetadata: [field],
        implemented: true,
        isCompatible: (song) => hasText(song[field]),
        getChoices: (ctx, song) => metadataChoices(ctx.songs, song, field),
        getCorrectAnswer: (song) => song[field],
        validateAnswer: (song, answer) => hasText(song[field]) && textsMatch(song[field], String(answer ?? "")),
        formatAnswer: (answer) => String(answer ?? ""),
        getPublicReveal: (song) => ({
            label: "Bonne réponse",
            value: song[field] ?? "—",
        }),
    };
}
const GENRE = makeMetadataCategory("GENRE", "Genre", "🎸", "#14b8a6", "genre", "Quel genre musical ?");
const COUNTRY = makeMetadataCategory("COUNTRY", "Nationalité", "🌍", "#6366f1", "country", "Pays / nationalité ?");
const LANGUAGE = makeMetadataCategory("LANGUAGE", "Langue", "🗣️", "#ec4899", "language", "Quelle langue ?");
const REGISTRY = {
    DECADE,
    YEAR_PM2,
    YEAR_PM4,
    YEAR_EXACT,
    BEFORE_2000,
    SOLO_GROUP,
    ARTIST,
    TITLE,
    GENRE,
    COUNTRY,
    LANGUAGE,
};
export function getBingoCategory(id) {
    const cat = REGISTRY[id];
    if (!cat)
        throw new Error(`Catégorie inconnue : ${id}`);
    return cat;
}
export function listBingoCategories() {
    return Object.values(REGISTRY);
}
export function toCategoryPublic(cat) {
    const { isCompatible, getChoices, getCorrectAnswer, validateAnswer, formatAnswer, getPublicReveal, isExactYearBonus, ...pub } = cat;
    void isCompatible;
    void getChoices;
    void getCorrectAnswer;
    void validateAnswer;
    void formatAnswer;
    void getPublicReveal;
    void isExactYearBonus;
    return pub;
}
export function songsCompatibleWith(categoryId, songs) {
    const cat = getBingoCategory(categoryId);
    return songs.filter((s) => cat.isCompatible(s));
}
